import React, { useRef, useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import type { ViewType } from '../data/homepageData';

interface ViewToggleProps {
  view: ViewType;
  onChange: (view: ViewType) => void;
}

/**
 * Creator / Brand toggle with a sliding animated background pill.
 * Uses refs to measure button positions so the slider always tracks exactly.
 */
const ViewToggle: React.FC<ViewToggleProps> = ({ view, onChange }) => {
  const creatorRef = useRef<HTMLButtonElement>(null);
  const brandRef = useRef<HTMLButtonElement>(null);
  const [sliderStyle, setSliderStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    const activeRef = view === 'creator' ? creatorRef : brandRef;
    const el = activeRef.current;
    if (el) {
      setSliderStyle({
        left: el.offsetLeft + 'px',
        width: el.offsetWidth + 'px',
      });
    }
  }, [view]);

  return (
    <div className="flex justify-center mb-0">
      <div
        className="flex items-center relative p-1"
        style={{
          background: 'var(--cv-surface)',
          border: '1px solid var(--cv-border)',
          borderRadius: 'var(--radius-full)',
        }}
      >
        {/* Animated slider background */}
        <div className="cv-toggle-slider" style={sliderStyle} aria-hidden="true" />

        <button
          ref={creatorRef}
          className={cn(
            'relative z-[2] px-7 py-2.5 text-sm font-semibold transition-colors duration-300 cursor-pointer border-none bg-transparent',
            'rounded-full',
          )}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-sm)',
            color: view === 'creator' ? '#fff' : 'var(--cv-text-muted)',
          }}
          onClick={() => onChange('creator')}
          aria-pressed={view === 'creator'}
        >
          ✦ For Creators
        </button>

        <button
          ref={brandRef}
          className={cn(
            'relative z-[2] px-7 py-2.5 text-sm font-semibold transition-colors duration-300 cursor-pointer border-none bg-transparent',
            'rounded-full',
          )}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-sm)',
            color: view === 'brand' ? '#fff' : 'var(--cv-text-muted)',
          }}
          onClick={() => onChange('brand')}
          aria-pressed={view === 'brand'}
        >
          ◆ For Brands
        </button>
      </div>
    </div>
  );
};

export default ViewToggle;
